/**
 * MetaProbe Blank/Black Frame Detector
 *
 * Decodes frames from the head and tail of a video and flags trailing/leading
 * black or solid-flat ("blank") frames — a common shot-QC defect introduced by
 * renders, transcodes, and exports.
 *
 * Pure client-side: uses a hidden <video> + a tiny offscreen <canvas>. No deps.
 *
 * IMPORTANT: a browser <video> can only produce pixels for codecs it can play
 * (roughly H.264 / VP9 / AV1 in MP4 / WebM). ProRes (MOV), AVI and MKV usually
 * won't decode, leaving the canvas black — which would look like an all-black
 * clip. We guard against that with a decodability check and a mid-clip probe so
 * we never emit a false "trailing black frames" result.
 */

// --- Tunable thresholds (0-255 luma scale) ------------------------------------
const BLACK_LUMA_P98 = 16;   // 98th-percentile luma below this => "black"
const FLAT_STDDEV = 6;       // spatial stddev below this (and not black) => "flat/blank"
const MAX_SCAN_SECONDS = 3;  // how far in from each end we look
const DOWNSCALE_MAX_SIDE = 64; // longest canvas side for luma sampling
const SEEK_TIMEOUT_MS = 4000;  // per-seek guard so a stuck decode can't hang us

/**
 * Derive fps / duration / frame count from an extracted metadata object.
 * Returns numbers (with sensible fallbacks) for driving the scan.
 */
export function extractTiming(metadata) {
    if (!metadata) return { fps: 30, durationSeconds: null, frameCount: null };

    const general = metadata.videoMetadata?.General || metadata.General;
    const video = metadata.videoMetadata?.Video || metadata.Video;

    // frameRate is stored like "24.000 fps"
    let fps = null;
    if (metadata.frameRate) {
        const parsed = parseFloat(String(metadata.frameRate));
        if (Number.isFinite(parsed) && parsed > 0) fps = parsed;
    }

    const durationSeconds = general?.durationSeconds
        ?? (metadata.duration ? parseFloat(String(metadata.duration)) : null);

    const frameCount = video?.numberOfSamples
        ?? metadata.AllTracks?.[0]?.numberOfSamples
        ?? null;

    // Fall back to deriving fps from frame count / duration, then a safe default.
    if (!fps && frameCount && durationSeconds) fps = frameCount / durationSeconds;
    if (!fps || !Number.isFinite(fps)) fps = 30;

    return { fps, durationSeconds: durationSeconds || null, frameCount: frameCount || null };
}

/**
 * Analyze a video file for blank/black frames at the head and tail.
 *
 * @param {File} file
 * @param {{ fps?: number, durationSeconds?: number }} [timing]
 * @returns {Promise<{
 *   decodable: boolean,
 *   wholeClip: boolean,
 *   head: { count: number, seconds: number, type: 'black'|'flat'|'mixed' } | null,
 *   tail: { count: number, seconds: number, type: 'black'|'flat'|'mixed' } | null,
 *   fps: number,
 *   reason?: string,
 * }>}
 */
export async function analyzeBlankFrames(file, timing = {}) {
    const fps = timing.fps && timing.fps > 0 ? timing.fps : 30;
    const result = {
        decodable: false,
        wholeClip: false,
        head: null,
        tail: null,
        fps,
    };

    if (!file || !file.type?.startsWith('video/')) {
        result.reason = 'not-video';
        return result;
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    video.src = url;
    // Keep it out of layout but still decodable.
    video.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(video);

    try {
        const ok = await waitForMetadata(video);
        if (!ok || !video.videoWidth || !video.videoHeight) {
            result.reason = 'undecodable-codec';
            return result;
        }

        const duration = Number.isFinite(video.duration) && video.duration > 0
            ? video.duration
            : timing.durationSeconds || 0;
        if (!duration) {
            result.reason = 'unknown-duration';
            return result;
        }

        // Offscreen sampling canvas, aspect-preserved.
        const scale = DOWNSCALE_MAX_SIDE / Math.max(video.videoWidth, video.videoHeight);
        const cw = Math.max(1, Math.round(video.videoWidth * scale));
        const ch = Math.max(1, Math.round(video.videoHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        const frameDur = 1 / fps;
        const totalFrames = Math.max(1, Math.round(duration * fps));
        const maxScan = Math.min(totalFrames, Math.ceil(MAX_SCAN_SECONDS * fps));

        // Mid-clip probe: distinguishes "genuinely blank / non-decoding clip" from
        // "clip with a blank tail". If the middle frame is also blank, we don't
        // claim a trailing/leading defect.
        const midStats = await sampleAt(video, ctx, cw, ch, duration / 2);
        if (!midStats) {
            result.reason = 'decode-failed';
            return result;
        }
        result.decodable = true;
        const midFlag = classify(midStats);

        // Scan the tail (inward from the end) and head (inward from the start).
        const tail = await scanEnd(video, ctx, cw, ch, maxScan, frameDur, (k) =>
            duration - (k - 0.5) * frameDur);
        const head = await scanEnd(video, ctx, cw, ch, maxScan, frameDur, (k) =>
            (k - 0.5) * frameDur);

        // If the middle frame is itself blank AND both ends ran to the scan limit,
        // the whole clip is blank/black (or not truly decoding) — report that
        // instead of a misleading "trailing frames" count.
        if (midFlag.flagged && tail.count >= maxScan && head.count >= maxScan) {
            result.wholeClip = true;
            return result;
        }

        result.head = head.count > 0
            ? { count: head.count, seconds: round3(head.count * frameDur), type: head.type }
            : null;
        result.tail = tail.count > 0
            ? { count: tail.count, seconds: round3(tail.count * frameDur), type: tail.type }
            : null;

        return result;
    } catch (e) {
        console.warn('Blank-frame analysis failed:', e);
        result.reason = 'error';
        return result;
    } finally {
        video.removeAttribute('src');
        video.load();
        video.remove();
        URL.revokeObjectURL(url);
    }
}

/** Walk inward from one end, counting consecutive flagged frames. */
async function scanEnd(video, ctx, cw, ch, maxScan, frameDur, timeForK) {
    let count = 0;
    let sawBlack = false;
    let sawFlat = false;

    for (let k = 1; k <= maxScan; k++) {
        const t = clamp(timeForK(k), 0, video.duration);
        const stats = await sampleAt(video, ctx, cw, ch, t);
        if (!stats) break; // decode hiccup — stop conservatively
        const f = classify(stats);
        if (!f.flagged) break; // hit real content
        count++;
        if (f.type === 'black') sawBlack = true;
        else sawFlat = true;
    }

    const type = sawBlack && sawFlat ? 'mixed' : sawBlack ? 'black' : 'flat';
    return { count, type };
}

/** Seek to time t, draw the frame downscaled, return luma stats (or null). */
async function sampleAt(video, ctx, cw, ch, t) {
    const seeked = await seekTo(video, t);
    if (!seeked) return null;
    try {
        ctx.drawImage(video, 0, 0, cw, ch);
        const { data } = ctx.getImageData(0, 0, cw, ch);
        return lumaStats(data);
    } catch (e) {
        // SecurityError (tainted canvas) or transient decode error
        return null;
    }
}

/** Set currentTime and resolve once the frame is ready (with a timeout guard). */
function seekTo(video, t) {
    return new Promise((resolve) => {
        let done = false;
        const finish = (ok) => {
            if (done) return;
            done = true;
            video.removeEventListener('seeked', onSeeked);
            video.removeEventListener('error', onError);
            clearTimeout(timer);
            if (!ok) return resolve(false);
            // Give the compositor a beat to present the seeked frame before we draw.
            requestAnimationFrame(() => resolve(true));
        };
        const onSeeked = () => finish(true);
        const onError = () => finish(false);
        const timer = setTimeout(() => finish(false), SEEK_TIMEOUT_MS);
        video.addEventListener('seeked', onSeeked);
        video.addEventListener('error', onError);
        try {
            video.currentTime = t;
        } catch (e) {
            finish(false);
        }
    });
}

function waitForMetadata(video) {
    return new Promise((resolve) => {
        if (video.readyState >= 1 && video.videoWidth) return resolve(true);
        let done = false;
        const finish = (ok) => {
            if (done) return;
            done = true;
            video.removeEventListener('loadedmetadata', onMeta);
            video.removeEventListener('error', onError);
            clearTimeout(timer);
            resolve(ok);
        };
        const onMeta = () => finish(true);
        const onError = () => finish(false);
        const timer = setTimeout(() => finish(false), SEEK_TIMEOUT_MS);
        video.addEventListener('loadedmetadata', onMeta);
        video.addEventListener('error', onError);
    });
}

/** Per-pixel luma mean / stddev / 98th-percentile via a 256-bin histogram. */
function lumaStats(rgba) {
    const hist = new Uint32Array(256);
    let sum = 0;
    let sumSq = 0;
    let n = 0;
    for (let i = 0; i < rgba.length; i += 4) {
        // Rec.709 luma
        const y = (0.2126 * rgba[i] + 0.7152 * rgba[i + 1] + 0.0722 * rgba[i + 2]) | 0;
        hist[y]++;
        sum += y;
        sumSq += y * y;
        n++;
    }
    if (n === 0) return { mean: 0, stddev: 0, p98: 0 };
    const mean = sum / n;
    const variance = Math.max(0, sumSq / n - mean * mean);
    const stddev = Math.sqrt(variance);

    const p98Target = n * 0.98;
    let cum = 0;
    let p98 = 255;
    for (let v = 0; v < 256; v++) {
        cum += hist[v];
        if (cum >= p98Target) { p98 = v; break; }
    }
    return { mean, stddev, p98 };
}

/** Classify a frame from its luma stats. */
function classify({ p98, stddev }) {
    if (p98 < BLACK_LUMA_P98) return { flagged: true, type: 'black' };
    if (stddev < FLAT_STDDEV) return { flagged: true, type: 'flat' };
    return { flagged: false, type: 'content' };
}

function clamp(x, lo, hi) { return Math.min(hi, Math.max(lo, x)); }
function round3(x) { return Math.round(x * 1000) / 1000; }

export default analyzeBlankFrames;
